/**
 * StoreOperator — AI Search Grid  ·  Modern UI v2
 * Intercepts the live-search flydown and re-renders results
 * as a responsive icon grid, ranked by AI relevance scoring.
 */

(function () {
  'use strict';

  /* ── AI Relevance Scorer ──────────────────────────────────── */
  // Predictive results are bounded; rank those results by title, phrase, and model.
  // Full search results use Shopify's relevance sort across all pages instead.
  function matchScore(item, queryWords) {
    const title = String(item.title || '').toLowerCase();
    const vendor = String(item.vendor || '').toLowerCase();
    const type = String(item.type || '').toLowerCase();
    const phrase = queryWords.join(' ').toLowerCase().trim();
    if (!phrase) return 0;
    const compact = function (s) { return s.replace(/[^a-z0-9]/g, ''); };
    const model = compact(phrase);
    const titleModel = compact(title);
    const words = queryWords.filter(function (word) { return word.length >= 2; });
    let score = 0;
    if (title === phrase) score += 1000;
    else if (title.startsWith(phrase)) score += 600;
    else if (title.includes(phrase)) score += 400;
    if (model.length >= 3 && titleModel.includes(model)) score += 260;
    if (words.length > 1 && words.every(function (word) {
      return title.includes(word) || titleModel.includes(compact(word));
    })) score += 130;
    words.forEach(function (word) {
      if (title.includes(word)) score += 25;
      if (new RegExp('\\b' + escRe(word), 'i').test(title)) score += 35;
      if (vendor.includes(word)) score += 6;
      if (type.includes(word)) score += 4;
    });
    if (score > 0 && item.available) score += 1;
    return score;
  }

  function escRe(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /* ── Badge helper ─────────────────────────────────────────── */
  function getBadge(item, topScore) {
    if (!item.available) return { label: 'Sold Out', cls: 'so-badge--soldout' };
    if (item.compare_at_price && item.compare_at_price > item.price)
      return { label: 'Sale', cls: 'so-badge--sale' };
    if (item._score >= topScore * 0.95)
      return { label: '⚡ Best Match', cls: 'so-badge--best' };
    return null;
  }

  /* ── Price formatter ──────────────────────────────────────── */
  function fmt(cents) {
    if (!cents && cents !== 0) return '';
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ── Build full panel HTML ────────────────────────────────── */
  function buildGrid(results, query) {
    const viewAllUrl = '/search?q=' + encodeURIComponent(query) + '&type=product';

    // Panel header
    let html = '<div class="so-panel-header">';
    html += '<span class="so-panel-title">Search Results</span>';
    html += '<span class="so-panel-ai-badge">Best Match</span>';
    html += '</div>';

    // Ask-the-AI row: carries the question to the search page, which auto-asks
    html += '<a class="so-ask-ai-row" href="/search?q=' + encodeURIComponent(query) + '&type=product&ask=1">' +
      '<span class="so-ask-ai-badge">BG AI+</span>' +
      '<span class="so-ask-ai-text">Ask AI: &ldquo;' + escHtml(query) + '&rdquo;</span>' +
      '<span class="so-ask-ai-arrow">&rarr;</span></a>';

    if (!results || results.length === 0) {
      html += '<div class="so-grid-wrap"><div class="so-grid-empty">No results found for <strong>' +
        escHtml(query) + '</strong></div></div>';
      return html;
    }

    const queryWords = query.toLowerCase().split(/\s+/).filter(Boolean);

    // Score & sort
    results.forEach(function (r) { r._score = matchScore(r, queryWords); });
    results.sort(function (a, b) { return b._score - a._score; });

    const topScore = results[0]._score || 1;
    const hasMore  = results.length > 8;

    // Grid
    html += '<div class="so-grid-wrap">';
    html += '<div class="so-ai-grid' + (hasMore ? ' so-collapsed' : '') + '">';

    results.forEach(function (item) {
      const badge = getBadge(item, topScore);
      const img = item.image
        ? '<img src="' + escAttr(item.image) + '" alt="' + escAttr(item.title) + '" loading="lazy">'
        : '<div class="so-grid-no-img"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg></div>';

      html += '<a class="so-grid-card' + (item.available ? '' : ' so-grid-card--unavailable') + '" href="' + escAttr(item.url) + '">';
      html += '<div class="so-grid-img">' + img;
      if (badge) html += '<span class="so-badge ' + badge.cls + '">' + badge.label + '</span>';
      html += '</div>';
      html += '<div class="so-grid-info">';
      html += '<p class="so-grid-title">' + escHtml(item.title) + '</p>';
      if (item.vendor) html += '<p class="so-grid-vendor">' + escHtml(item.vendor) + '</p>';
      html += '<div class="so-grid-price">';
      if (item.compare_at_price && item.compare_at_price > item.price) {
        html += '<span class="so-grid-compare">' + fmt(item.compare_at_price) + '</span>';
      }
      html += '<span class="so-grid-current">' + fmt(item.price) + '</span>';
      html += '</div>';
      html += '</div>';
      html += '</a>';
    });

    html += '</div>'; // .so-ai-grid
    html += '</div>'; // .so-grid-wrap

    // Orange gradient divider
    html += '<div class="so-grid-divider"></div>';

    // Footer
    html += '<div class="so-grid-footer">';
    if (hasMore) {
      html += '<button class="so-grid-viewmore" type="button" data-so-viewmore>' +
        '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M8 3v10M3 8l5 5 5-5"/></svg>' +
        'View More &nbsp;·&nbsp; ' + (results.length - 8) + ' more' +
        '</button>';
    } else {
      html += '<span></span>';
    }
    html += '<a class="so-grid-viewall so-grid-viewall--button" href="' + escAttr(viewAllUrl) + '">' +
      'View all results for <strong>' + escHtml(query) + '</strong> →' +
      '</a>';
    html += '</div>'; // .so-grid-footer

    return html;
  }

  /* ── "View More" click handler ────────────────────────────── */
  function bindViewMore(flydown) {
    flydown.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-so-viewmore]');
      if (!btn) return;
      const grid = flydown.querySelector('.so-ai-grid');
      if (grid) {
        grid.classList.remove('so-collapsed');
        btn.style.display = 'none';
      }
    });
  }

  function escHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function escAttr(s) { return escHtml(s || ''); }

  // Check link structure, not title/image/price: a genuine $0 product still counts.
  function isProductHref(href) {
    return typeof href === 'string' &&
      /^\/products\/[a-z0-9][a-z0-9-]*\/?(?:[?#]|$)/i.test(href);
  }

  /* ── MutationObserver ─────────────────────────────────────── */
  function observe() {
    const flydowns = document.querySelectorAll('[data-live-search-results]');
    if (!flydowns.length) return;

    flydowns.forEach(function (flydown) {
      if (flydown._soObserving) return;
      flydown._soObserving = true;
      bindViewMore(flydown);

      const obs = new MutationObserver(function () {
        const raw = flydown.innerHTML.trim();
        if (!raw || raw.includes('so-ai-grid') || raw.includes('so-panel-header')) return;

        // Try JSON data block
        const dataEl = flydown.querySelector('[data-search-results-json]');
        let items = null;
        if (dataEl) {
          try { items = JSON.parse(dataEl.textContent); } catch (e) {}
        }

        // Fallback: scrape rendered links
        if (!items) {
          items = [];
          flydown.querySelectorAll('a.search-flydown--product[href]').forEach(function (a) {
            if (!isProductHref(a.getAttribute('href'))) return;
            const imgEl   = a.querySelector('img');
            const titleEl = a.querySelector('.search-flydown--product-title, [class*="title"]');
            const priceEl = a.querySelector('.search-flydown--product-price, .money, [class*="price"]');
            const rawPrice = priceEl ? priceEl.textContent.replace(/[^0-9.]/g, '') : '';
            items.push({
              title    : titleEl ? titleEl.textContent.trim() : a.textContent.trim(),
              url      : a.getAttribute('href'),
              image    : imgEl ? imgEl.src : null,
              price    : rawPrice ? Math.round(parseFloat(rawPrice) * 100) : 0,
              available: !a.classList.contains('sold-out'),
              vendor   : '',
              type     : ''
            });
          });
        }

        items = items.filter(function (item) { return item && isProductHref(item.url); });
        if (!items.length) return;

        const form  = flydown.closest('[data-live-search-form]') || flydown.closest('form');
        const input = form ? form.querySelector('[data-live-search-input], input[name="q"]') : null;
        const query = input ? input.value.trim() : '';

        flydown.innerHTML = buildGrid(items, query);
      });

      obs.observe(flydown, { childList: true, subtree: false });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }
  document.addEventListener('shopify:section:load', observe);

})();

/* storeoperator-ai-grid-product-cta-v1 */

/* storeoperator-search-title-model-match-v1 */
